import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
   ExtensionChipOptions,
   ExtensionOption,
} from 'src/app/pages/extension-register/models/extension-option.interface';
import { LcsSingleSelectSelectedEvent } from '../../single-select/single-select-base';

import { MatChipInputEvent } from '@angular/material/chips';
import { ExternalNumberDetail } from 'src/app/pages/extension-details/models/extension-detail-view.model';
import { formatPhoneNumbers } from 'src/app/pipes/format-phone-numbers/format-phone-numbers.pipe';
import { ExtensionAndExternalNumbersSelectComponent } from '../component/extension-and-external-numbers-select.component';
import { ChipInputOption, ChipInputType } from './extension-and-group.model';

@Component({
    providers: [
        { multi: true, provide: NG_VALUE_ACCESSOR, useExisting: PhoneExtensionAndExternalNumbersChipInputComponent },
    ],
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-and-external-numbers-chip-input',
    styleUrls: ['./extension-and-external-numbers-chip-input.component.scss'],
    templateUrl: './extension-and-external-numbers-chip-input.component.html',
    standalone: false
})
export class PhoneExtensionAndExternalNumbersChipInputComponent implements ControlValueAccessor, OnInit {
   @Input() disabled: boolean = false;

   @Input() required: boolean = false;

   @Input() externalNumbers: ExternalNumberDetail[] = [];

   @Input() maxChipCount: number | null;

   @Input() set extensions(extensions: ExtensionChipOptions[]) {
      if (extensions !== undefined) {
         this._extensions = extensions.filter((extension) => {
            if (extension.hasOwnProperty('isArchived')) {
               return extension.isArchived === false;
            } else return true;
         });
      }
   }

   get extensions(): ExtensionChipOptions[] {
      return this._extensions;
   }

   @Input() form: UntypedFormGroup;

   @Input() extensionIdsToExclude: number[] = [];

   @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;

   @ViewChild('groupSelect') groupSelect: ExtensionAndExternalNumbersSelectComponent;

   chipsControl = new UntypedFormControl();

   inputControl = new UntypedFormControl();

   values: ChipInputOption[] = [];

   availableExtensions: ExtensionOption[] = [];

   filteredExtensions: ExtensionOption[] = [];

   filteredPhoneNumubers: ExternalNumberDetail[] = [];

   private _extensions: ExtensionChipOptions[];

   searchTerm: string = '';

   separatorKeysCodes: number[] = [ENTER, COMMA];

   onChange = (values: ChipInputOption[]) => {};

   onTouched: () => void;

   get displayValues(): ChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   ngOnInit() {}

   writeValue(value: ChipInputOption[]): void {
      if (value) {
         this.values = value;
      } else {
         this.values = [];
      }
      this.chipsControl.setValue(value);

      this.removeErrors();

      this.updateExtensionOptions();
      this.updateExternalNumberOptions();
   }

   registerOnChange(onChange: (values: ChipInputOption[]) => void) {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   ngAfterViewInit(): void {
      this.updateExtensionInputControlDisabledState();
   }

   removeValue(value: ChipInputOption): void {
      const index = this.values.findIndex((v) => v.id === value.id && v.type === value.type && v.isDeleted === false);
      if (index !== -1) {
         if (this.values[index].id === 0) {
            this.values.splice(index, 1);
         } else {
            this.values[index].isDeleted = true;
         }
         this.updateValueAndControls();
      }
   }

   add(event: MatChipInputEvent): void {
      //Don't add if there are more active chips than allowed
      if (this.maxChipCount && this.displayValues.length > this.maxChipCount) {
         return;
      }
      if (this.groupSelect.isOpen) {
         const value = event.value;
         const trimmedValue = value.trim();
         if (trimmedValue) {
            const extensionOptionComponent = this.groupSelect.options.find((o) => {
               return (
                  (o.value && o.value.number && o.value.number.toString() === trimmedValue) ||
                  (o.value && o.value.name && o.value.name.toLowerCase() === trimmedValue.toLowerCase()) ||
                  false
               );
            });
            if (extensionOptionComponent && extensionOptionComponent.value) {
               extensionOptionComponent.selectViaInteraction();
            } else {
               if (trimmedValue.match(/^(\d{3})(\d{3})(\d{4})$/)) {
                  const existingPhoneNumbers = this.values
                     .filter((value) => value.type === ChipInputType.PhoneNumber)
                     .map((v) => {
                        // Ensure it's an ExternalNumberDetail and has externalNumber
                        if (
                           v.value &&
                           typeof v.value === 'object' &&
                           'externalNumber' in v.value &&
                           typeof v.value.externalNumber === 'string'
                        ) {
                           return v.value.externalNumber;
                        }
                        if (typeof v.value === 'string') {
                           return v.value;
                        }

                        return '';
                     })
                     .filter((num) => num !== '');

                  if (existingPhoneNumbers.includes(trimmedValue)) {
                     this.showDuplicatePhoneNumberError();
                  } else {
                     const newExternalNumber: ExternalNumberDetail = {
                        externalNumberHistoryId: 0,
                        externalNumber: Number(trimmedValue),
                        accountId: 0,
                        number: 0,
                        name: '',
                     };
                     const newValue: ChipInputOption = {
                        type: ChipInputType.PhoneNumber,
                        value: newExternalNumber,
                        isDeleted: false,
                        id: 0,
                        extensionId: null,
                     };

                     this.values = [...this.values, newValue];
                     this.updateValueAndControls();
                  }
               } else {
                  this.showInvalidPhoneNumberError();
               }
            }
         }
      }
   }

   showDuplicatePhoneNumberError(): void {
      this.chipsControl.setErrors({ duplicatePhoneNumber: true });
      this.chipsControl.markAsTouched();
   }

   showInvalidPhoneNumberError(): void {
      this.chipsControl.setErrors({ invalidPhoneNumber: true });
      this.chipsControl.markAsTouched();
   }

   selected(event: LcsSingleSelectSelectedEvent<ExternalNumberDetail | ExtensionOption>): void {
      if (event.option.value) {
         const type = 'externalNumberHistoryId' in event.option.value ? 'PhoneNumber' : 'Extension';
         if (type == 'Extension') this.addChip(event.option.value, ChipInputType.Extension);
         if (type == 'PhoneNumber') this.addExternalNumberChip(event.option.value, ChipInputType.PhoneNumber);
      }
   }

   onInputChange(event: Event): void {
      this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
      this.filterOptions(this.searchTerm);
   }

   filterOptions(searchTerm: string): void {
      this.filteredExtensions = this.availableExtensions.filter(
         (extension) =>
            extension.number?.toString().toLowerCase().includes(searchTerm) ||
            extension.name?.toLowerCase().includes(searchTerm)
      );

      this.filteredPhoneNumubers = this.externalNumbers.filter((externalNumber) => {
         return externalNumber.externalNumber.toString().toLowerCase().includes(searchTerm);
      });

      if (this.groupSelect) {
         this.groupSelect.updateFilteredOptions();
      }
   }

   private addChip(value: any, type: ChipInputType): void {
      if (this.maxChipCount && this.displayValues.length >= this.maxChipCount) {
         return;
      }
      const existingChipIndex = this.values.findIndex((chip) => chip.id === value.extensionId);

      if (existingChipIndex > -1) {
         this.values[existingChipIndex].isDeleted = false;
         this.updateValueAndControls();
         return;
      }

      const newValue: ChipInputOption = {
         id: value.extensionId,
         type: type,
         extensionId: value.extensionId,
         value: value,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.updateValueAndControls();
   }
   private addExternalNumberChip(value: any, type: ChipInputType): void {
      if (this.maxChipCount && this.displayValues.length >= this.maxChipCount) {
         return;
      }

      const newValue: ChipInputOption = {
         id: value.externalNumberHistoryId,
         type: type,
         extensionId: null,
         value: value,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.updateValueAndControls();
   }

   removeErrors(): void {
      const hasChips = this.displayValues.length > 0;

      if (!hasChips && this.required) {
         this.chipsControl.setErrors({ required: true });
      } else {
         this.chipsControl.setErrors(null);
      }

      this.chipsControl.markAsTouched();
   }

   updateExtensionOptions() {
      const selectedExtensionIds = new Set(
         this.displayValues.filter((v) => v.type === ChipInputType.Extension).map((v) => v.extensionId)
      );

      this.availableExtensions = this.extensions.filter(
         (extension) =>
            !selectedExtensionIds.has(extension.extensionId) &&
            !this.extensionIdsToExclude.includes(extension.extensionId)
      );

      this.filteredExtensions = this.availableExtensions;
   }

   updateExternalNumberOptions() {
      const selectedExternalNumberIds = new Set(
         this.displayValues.filter((v) => v.type === ChipInputType.PhoneNumber).map((v) => v.id)
      );

      this.filteredPhoneNumubers = this.externalNumbers.filter(
         (externalNumber) => !selectedExternalNumberIds.has(externalNumber.externalNumberHistoryId)
      );
   }

   updateValueAndControls(): void {
      if (this.groupSelect.panel) {
         this.groupSelect.panel.visible = false;
      }

      this.chipInput.nativeElement.value = '';

      this.inputControl.setValue(null);

      this.chipsControl.setValue(this.displayValues);

      this.onChange(this.values);

      this.removeErrors();

      this.updateExtensionOptions();
      this.updateExternalNumberOptions();
      this.updateExtensionInputControlDisabledState();
   }

   @HostListener('document:click', ['$event'])
   onClickOutside(event: MouseEvent) {
      this.searchTerm = '';
      this.filterOptions(this.searchTerm);
   }

   getChipDisplay(value: ChipInputOption): string {
      if (value.type === ChipInputType.PhoneNumber) {
         if (typeof value.value === 'string') {
            let phoneNumber = formatPhoneNumbers(value.value);
            return `${phoneNumber} (Outside Line)`;
         }
         const externalNumberDetail = value.value as ExternalNumberDetail;
         if (externalNumberDetail && externalNumberDetail.externalNumber) {
            return `${formatPhoneNumbers(externalNumberDetail.externalNumber.toString())} (Outside Line)`;
         }

         return '';
      }
      const extension = value.value as ExtensionOption;
      const number = extension.number ?? '';
      const name = extension.name ?? '';

      return `${number} - ${name}`;
   }

   private updateExtensionInputControlDisabledState() {
      if (this.maxChipCount !== null && this.displayValues.length >= this.maxChipCount) {
         this.inputControl.disable();
      } else {
         this.inputControl.enable();
      }
   }
}
